import { useEffect, useState } from "react";
import BASE_URL from "../../BASEURL";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/order`);
      const data = await res.json();
      const ordersList = data.orders || data.data || [];
      setOrders(Array.isArray(ordersList) ? ordersList : []);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    const isConfirmed = window.confirm(`Are you sure you want to mark this order as ${status}?`);
    if (!isConfirmed) return;

    try {
      await fetch(`${BASE_URL}/api/order/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status })
      });
      fetchOrders();
    } catch (error) {
      console.error("Error updating order status:", error);
    }
  };

  // Helpers to handle different backend shapes
  const getProductsFromOrder = (order) => {
    return order.orderItems || order.items || order.products || order.cartItems || [];
  };

  const getProductName = (item) => {
    return (
      item.name ||
      item.title ||
      item.product?.name ||
      item.product?.title ||
      item.product?.productName ||
      item.productId?.name ||
      item.productId?.title ||
      item.productId?.productName ||
      item.productName ||
      "Unknown product"
    );
  };

  const getProductImage = (item) => {
    const maybe =
      item.image ||
      (item.images && item.images[0]) ||
      item.product?.image ||
      (item.product?.images && item.product.images[0]) ||
      item.product?.productImage ||
      item.productId?.image ||
      (item.productId?.images && item.productId.images[0]) ||
      item.productId?.productImage;

    if (!maybe) return null;
    if (typeof maybe === "string") return maybe;
    if (Array.isArray(maybe)) return maybe[0];
    return null;
  };

  const getProductQty = (item) => {
    return item.qty || item.quantity || item.count || item.cartQty || 1;
  };

  const getCustomerName = (order) => {
    return (
      order.userId?.name ||
      order.customerName ||
      order.address?.fullName ||
      order.addressDetails?.fullName ||
      order.shippingAddress?.fullName ||
      order.deliveryAddress?.fullName ||
      order.address?.name ||
      order.addressDetails?.name ||
      order.shippingAddress?.name ||
      order.deliveryAddress?.name ||
      "Unknown"
    );
  };

  const formatAddress = (order) => {
    const addr = order.shippingAddress || order.address || order.shipping || order.shipping_details || order.deliveryAddress || order.addressDetails || {};
    if (!addr) return "N/A";
    if (typeof addr === "string") return addr;
    const parts = [
      addr.fullName || addr.name || order.userId?.name || "",
      addr.houseNo || addr.address || addr.line1 || addr.street || "",
      addr.area || addr.line2 || "",
      addr.city || "",
      addr.state || "",
      addr.postalCode || addr.zip || addr.pincode || "",
      addr.country || ""
    ].filter(Boolean);
    return parts.join(", ");
  };

  if (loading) return <div>Loading orders...</div>;

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Orders Management</h2>
      <div className="bg-white border rounded overflow-x-auto">
        <table className="w-full text-sm text-left border-collapse">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="p-2 border-r">Order ID</th>
              <th className="p-2 border-r">Customer</th>
              <th className="p-2 border-r">Address</th>
              <th className="p-2 border-r">Products</th>
              <th className="p-2 border-r">Date</th>
              <th className="p-2 border-r">Total</th>
              <th className="p-2 border-r">Status</th>
              <th className="p-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => {
              const products = getProductsFromOrder(order);
              return (
                <tr key={order._id || order.id} className="border-b hover:bg-gray-50 align-top">
                  <td className="p-2 border-r align-top">{order._id || order.id}</td>
                  <td className="p-2 border-r align-top">
                    <div className="font-semibold">{getCustomerName(order)}</div>
                    <div className="text-xs text-gray-500">{order.userId?.email || order.email || order.address?.phone || order.address?.email || ""}</div>
                  </td>
                  <td className="p-2 border-r align-top w-48">
                    <div className="text-sm">{formatAddress(order)}</div>
                  </td>
                  <td className="p-2 border-r align-top">
                    {products.length === 0 && <div className="text-gray-500">No products listed</div>}
                    {products.map((item, idx) => (
                      <div key={idx} className="flex items-center space-x-3 py-1">
                        {getProductImage(item) ? (
                          <img src={getProductImage(item)} alt={getProductName(item)} className="w-12 h-12 object-cover rounded" />
                        ) : (
                          <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center text-xs text-gray-500">No Image</div>
                        )}
                        <div>
                          <div className="font-medium">{getProductName(item)}</div>
                          <div className="text-xs text-gray-500">
                            Qty: {getProductQty(item)} {item.discountPrice || item.productId?.discountPrice || item.product?.discountPrice ? ` • ₹${item.discountPrice || item.productId?.discountPrice || item.product?.discountPrice}` : ""}
                          </div>   
                        </div>
                      </div>
                    ))}
                  </td>
                  <td className="p-2 border-r align-top">{order.createdAt ? new Date(order.createdAt).toLocaleString() : "N/A"}</td>
                  <td className="p-2 border-r align-top">₹{order.totalPrice ?? order.total ?? 0}</td>
                  <td className="p-2 border-r font-bold align-top">{order.status || "Pending"}</td>
                  <td className="p-2 space-x-2 align-top">
                    <button onClick={() => updateStatus(order._id || order.id, "Shipped")} className="text-blue-600 hover:underline">Ship</button>
                    <button onClick={() => updateStatus(order._id || order.id, "Delivered")} className="text-green-600 hover:underline">Deliver</button>
                    <button onClick={() => updateStatus(order._id || order.id, "Cancelled")} className="text-red-600 hover:underline">Cancel</button>
                  </td>
                </tr>
              );
            })}
            {orders.length === 0 && (
              <tr><td colSpan="8" className="p-4 text-center">No orders found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}